import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import MaestroLoader from "@components/MaestroLoader";
import { useMutation, useQuery } from "@tanstack/react-query";
import { authenticatedApi } from "api";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
// import GridLayout from "react-grid-layout";
import GridLayout, { Responsive, WidthProvider } from "react-grid-layout";
import Sheet from '@mui/joy/Sheet';
import DashboardWidget from '@components/widgets/DashboardWidget';
import { Box, Button, ButtonGroup, Checkbox, Stack, Switch, Tooltip, Typography } from '@mui/joy';
import Dropdown from '@mui/joy/Dropdown';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListDivider from '@mui/joy/ListDivider';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import DashboardWidthProvider from '@providers/DashboardWidthProvider';
import { useDocumentTitle, useWindowSize } from '@uidotdev/usehooks';
import Subheader from '@components/layout/Subheader';
import { useAuth } from '@providers/AuthProvider';
import Table from '@mui/joy/Table';
import EnhancedTableHead from "@components/EnhancedTableHead";
import Paginate from "@components/Paginate";
import CreateNewButton from "@components/CreateNewButton";
import ToggleViewMode from "@components/ToggleViewMode";
import ToggleMultiSelect from "@components/ToggleMultiSelect";
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ObjectPreview from "./ObjectPreview";
import { useRestore } from "@api/crudService/useRestore";
import { detectObjectType } from "@utils/detectObjectType";
import pluralize from 'pluralize';
import toast from "react-hot-toast";
import UnrecoverableError from "@components/UnrecoverableError";
import { CheckPermission } from "@components/CheckPermission";
import { useDelete } from "@api/crudService";
import { events } from "@utils/analytics";

const defaultViewModes = [
    {
        name: 'active',
        label: 'Active',// 'All',
    },
    {
        name: 'deleted',
        label: 'Deleted',
    },
];

export default function ObjectIndex({
    query,
    deleteMutation = null,
    type = '',
    permissionType = null,
    title,
    color,
    columns,
    listCell,
    listActions = () => {},
    additionalMoreActions = null,
    additionalHeaderActions = null,
    defaultParams:_defaultParams = { order: 'asc', orderby: 'name', page: 1, view: 'active' },
    children,
    viewModes = defaultViewModes,
    ...restProps
}) {
    useDocumentTitle(`${title} | Maestro Community Manager`);

    const { navigate } = useNavigate();

    const [viewMode, _setViewMode] = useState('active');
    const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);
    const [selected, setSelected] = useState([]);

    const [chooseViewModeOpen, setChooseViewModeOpen] = useState(false);

    // deselect everything when we toggle the mode
    useEffect(() => {
        setSelected([]);
    }, [multiSelectEnabled]);


    const defaultParams = {
        order: 'asc',
        orderby: 'name',
        page: 1,
        view: 'active',
        ..._defaultParams,
    };

    // todo: handle filters/sorts
    // let { order = 'asc', orderby = 'name', page = 1 } = useParams();
    const [searchParams, setSearchParams] = useSearchParams(defaultParams);

    // todo: probably move this to state w/ an effect, which would also solve pagination data undefined
    const order = searchParams.get('order') ?? defaultParams.order;
    const orderby = searchParams.get('orderby') ?? defaultParams.orderby;
    const page = searchParams.get('page') ?? defaultParams.page;
    // console.log(page, searchParams.get('page'), defaultParams.page);
    const view = searchParams.get('view') ?? defaultParams.view;

    const currentViewMode = viewModes.find(mode => mode.name === view);

    // filter out columns that shouldn't display on deleted screen
    columns = columns.filter(column => {
        if(column?.showOnDeleted === false && view === 'deleted') {
            return false;
        }

        return true;
    });

    const { user, can } = useAuth();

    const {
        // isPending,
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
    } = query(
        {page, orderby, order, view, ...(currentViewMode?.filters ?? {})},
        {
            retry: false,
        }
    );

    const handleRequestSort = (
        event,//: React.MouseEvent<unknown>,
        property,//: keyof Data,
    ) => {
        const isAsc = orderby === property && order === 'asc';

        setSearchParams({
            ...searchParams,
            page,
            view,
            order: isAsc ? 'desc' : 'asc',
            orderby: property,
        });

        // setOrder();
        // setOrderBy(property);
    };

    const setViewMode = (newViewMode) => {
        setMultiSelectEnabled(false);
        setSearchParams({
            ...searchParams,
            ...defaultParams,
            view: newViewMode,//view === 'active' ? 'deleted' : 'active',
        });
    }

    const isSelected = (id) => {
        return selected.indexOf(id) !== -1;
    }

    const isEntirePageSelected = () => {
        if(!data?.data) return false;

        for(const row of data.data) {
            if(selected.indexOf(row.id) === -1) {
                return false;
            }
        }

        return true;
    }

    const toggleSelect = (id) => {
        let _selected = [...selected];
        if(selected.indexOf(id) !== -1) {
            _selected.splice(_selected.indexOf(id), 1);
        } else {
            _selected.push(id);
        }

        setSelected(_selected);
    }

    const toggleSelectAll = () => {
        if(!data?.data) return false;
        let _selected = [...selected];

        if(isEntirePageSelected()) {
            for(const row of data.data) {
                let recordIndex = _selected.indexOf(row.id);
                if(recordIndex !== -1) {
                    _selected.splice(recordIndex, 1);
                }
            }
        } else {
            for(const row of data.data) {
                let recordIndex = _selected.indexOf(row.id);
                if(recordIndex === -1) {
                    _selected.push(row.id);
                }
            }
        }

        setSelected(_selected);
    }

    const deleteAction = deleteMutation ? deleteMutation({id: -1}) : ()=>{};// useDelete({queryKey: [endpoint]});
    const onDeleteSelected = () => {
        const action = deleteAction.mutateAsync({ids: selected.join(',')});
        toast.promise(action, {
            loading: 'Deleting...',
            success: 'Successfully deleted',
            error: 'There was an error while deleting.',
        }, {
            success: {
                duration: 5000,
                // icon: '🔥',
            },
        });
        action.then(() => {
            setSelected([]);
            setMultiSelectEnabled(false);
        });
    };

    if(isLoading) {
        return (<>
            <Subheader
                title={title}
                subtitle={currentViewMode.label}
                tint={color}
            >
                {/* This icon button is used as placeholder to keep the page from changing height */}
                <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    // onClick={onClick}
                    sx={{ bgcolor: 'background.surface', opacity: 0, pointerEvents: 'none' }}
                >
                    <ChevronRightRoundedIcon />
                </IconButton>
            </Subheader>
            <MaestroLoader centered/>
        </>
        );
    }

    if(isError) {
        return (<UnrecoverableError status={403} />)
    }

    const LinkComponent = view === 'deleted' ? Box : ObjectPreview;

    const RestoreAction = ({row}) => {
        const navigate = useNavigate();
        let objectType = detectObjectType(row);
        if(objectType === 'workplan') {
            objectType = 'work-plan';
        }
        let endpoint = pluralize(objectType);
        const restoreAction = useRestore({queryKey: [endpoint, row.id]});
        const onRestore = () => {
            const action = restoreAction.mutateAsync();
            toast.promise(action, {
                loading: 'Restoring...',
                success: (t) => (
                    <Box sx={{ marginRight: '-10px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        The {objectType.replace('-', ' ')} was restored!
                        <br/>
                        <Button
                            size="sm"
                            variant="solid"
                            color="success"
                            // component={Link}
                            // to={`${row.id}`}
                            // relative={true}
                            onClick={() => {
                                toast.dismiss(t.id);
                                navigate(`${row.id}`);
                            }}
                        >
                            View
                        </Button>
                    </Box>
                ),
                error: 'There was an error while restoring.',
            }, {
                success: {
                    duration: 5000,
                    // icon: '🔥',
                },
            });

            action.then(() => {
                events.objectRestored({ type: type });
            });
        };
        return (
        // <LinkComponent to={`${row.id}`} relative={true}>
            <Tooltip title="Restore" placement="left">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                onClick={onRestore}
                sx={{ bgcolor: 'background.surface' }}
                >
                <RestoreRoundedIcon />
            </IconButton>
            </Tooltip>
        // </LinkComponent>
    )
};

    const ViewAction = ({row}) => (
        <LinkComponent to={`${row.id}`} relative={true} object={row}>
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                // onClick={onClick}
                sx={{ bgcolor: 'background.surface' }}
                >
                <ChevronRightRoundedIcon />
            </IconButton>
        </LinkComponent>
    );

    const MoreAction = ({row}) => (
        <Dropdown>
            <MenuButton
                slots={{ root: IconButton }}
                slotProps={{ root: { variant: 'outlined', color: 'neutral', size: 'sm' } }}
            >
                <MoreVertRoundedIcon />
            </MenuButton>
            <Menu placement="bottom-end">
                <MenuItem component={Link} to={`${row.id}/edit`} relative={true}>Edit</MenuItem>
                {additionalMoreActions !== null && additionalMoreActions({row})}
            </Menu>
        </Dropdown>
    );

    // const PrimaryTableAction = ({row}) => view === 'deleted' ? <RestoreAction row={row}/> : <MoreAction row={row}/>;
    // const PrimaryListAction = ({row}) => view === 'deleted' ? <RestoreAction row={row}/> : <ViewAction row={row}/>;

    const PrimaryTableAction = view === 'deleted' ? RestoreAction : MoreAction;
    const PrimaryListAction = view === 'deleted' ? RestoreAction : ViewAction;

    return (<>
        <Subheader
            title={title}
            subtitle={currentViewMode.label}
            tint={color}
        >
            <Paginate
                tint={color}
                from={data.from}
                to={data.to}
                total={data.total}
                currentPage={data.current_page}
                perPage={data.per_page}
                lastPage={data.last_page}
                onChange={(newPage) => {
                    setSearchParams({
                        ...searchParams,
                        page: newPage,
                        order,
                        orderby,
                        view,
                    });
                }}
            />
            <CheckPermission permissions={`edit ${permissionType}`}>
                {!restProps?.disableActions && (
                <Stack sx={{gap: 1.5, flexDirection: 'row'}}>
                    <ButtonGroup
                        size="sm"
                        color="neutral"
                        variant="outlined"
                    >
                        {additionalHeaderActions !== null && additionalHeaderActions({data})}
                        {/* {view !== 'deleted' && ( */}
                        {deleteMutation !== null && (
                            <ToggleMultiSelect enabled={view !== 'deleted'} toggled={multiSelectEnabled} onChange={setMultiSelectEnabled} />
                        )}
                        {/* )} */}
                    </ButtonGroup>

                    <Dropdown>
                        <MenuButton
                            // slots={{ root: IconButton }}
                            // slotProps={{ root: { variant: 'outlined', color: 'neutral', size: 'sm' } }}
                            size="sm"
                            color="neutral"
                            variant="outlined"
                            sx={{ bgcolor: 'background.surface' }}
                            endDecorator={<KeyboardArrowDownRoundedIcon />}
                        >
                            {currentViewMode.label}
                        </MenuButton>
                        <Menu>
                            {viewModes.map(vM => (
                                <Tooltip key={vM.name} title={vM?.tooltip ?? ''} disableInteractive>
                                <MenuItem
                                    key={vM.name}
                                    selected={vM.name === view}
                                    onClick={()=>setViewMode(vM.name)}
                                >
                                    {vM.label}
                                </MenuItem>
                                </Tooltip>
                            ))}
                        </Menu>
                    </Dropdown>
                </Stack>
                )}
            </CheckPermission>
            <CheckPermission permissions={[`edit ${permissionType}`, `edit basic ${permissionType}`]}>
                <CreateNewButton color={color} />
            </CheckPermission>
        </Subheader>


        {/* Desktop Table */}
        <Box sx={theme => ({
            backgroundImage: color && `linear-gradient(180deg, ${theme.palette[color][100]} 0%, white 100%);`,// : null,
            backgroundAttachment: 'fixed',
            height: '100%',
            p: 2,
        })}>

        {children}



        <Sheet
            variant="outlined"
            sx={{
                display: { xs: 'none', sm: 'block' },
                // width: '100%',
                borderRadius: 'sm',
                flexShrink: 1,
                overflow: 'auto',
                minHeight: 0,
                // m: 2,
                bgcolor: 'white',
            }}
        >
                {/* <Box
                    color="neutral"
                    variant="outlined"
                    sx={{
                        fontSize: 'md',
                        gap: 0,
                        px: 1,
                        py: 1,
                        // height: multiSelectEnabled ? '40px' : 0,
                        // overflow: 'hidden',
                        transition: 'var(--mm-default-transition)'
                    }}
                >
                    <Typography>
                    FILTERS
                    </Typography>
                </Box> */}

            <Table
                aria-labelledby="tableTitle"
                stickyHeader
                // hoverRow
                stripe={'even'}
                sx={{
                    '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                    '--Table-headerUnderlineThickness': '1px',
                    '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                    '--TableRow-stripeBackground': 'var(--joy-palette-background-level1)',
                    '--TableCell-paddingY': '4px',
                    '--TableCell-paddingX': '8px',
                    bgcolor: 'white',
                }}
            >
                <EnhancedTableHead
                    order={order}
                    orderBy={orderby}
                    headCells={columns}
                    rowCount={data.data.length}
                    onSelectAllClick={toggleSelectAll}
                    numSelected={selected.length}
                    allSelected={isEntirePageSelected()}
                    onRequestSort={handleRequestSort}
                    multiSelect={multiSelectEnabled}
                    onDelete={onDeleteSelected}
                />
                <tbody>
                    {data.data.length === 0 && (
                        <tr>
                            <td colSpan={columns.length+1}>
                                <Typography fontSize={'sm'}>There is nothing here yet.</Typography>
                            </td>
                        </tr>
                    )}
                    {data.data.map(row => {
                        return (
                            <tr key={row.id}>
                                {multiSelectEnabled && (
                                    <td>
                                        <Checkbox
                                            checked={isSelected(row.id)}
                                            onChange={() => toggleSelect(row.id)}
                                            slotProps={{
                                                input: {
                                                // 'aria-labelledby': labelId,
                                                },
                                            }}
                                            sx={{ verticalAlign: 'top' }}
                                        />
                                    </td>
                                )}
                                {columns.map((column, index) => (
                                    <td
                                        key={index}
                                        onClick={() => multiSelectEnabled && toggleSelect(row.id)}
                                        style={{cursor: multiSelectEnabled ? 'pointer' : 'unset'}}
                                    >
                                    <Box
                                        sx={{
                                            ...(multiSelectEnabled?{
                                                pointerEvents: 'none',
                                                userSelect: 'none',
                                            }:{}),
                                        }}
                                    >
                                        {column.cell(row, {LinkComponent})}
                                    </Box>
                                </td>))}
                                <td>
                                    {!restProps?.disableActions && (
                                        <CheckPermission permissions={permissionType === null || `edit ${permissionType}`}>
                                            <Box sx={{pointerEvents: multiSelectEnabled ? 'none' : 'unset'}}>
                                                <PrimaryTableAction row={row}/>
                                            </Box>
                                        </CheckPermission>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </Table>
        </Sheet>
        </Box>

        {/* Mobile List */}
        <Box sx={{ m:2, display: { xs: 'block', sm: 'none' } }}>
            {data.data.map((row) => (
                <List
                    key={row.id}
                    size="sm"
                    sx={{
                        '--ListItem-paddingX': 0,
                    }}
                >
                <ListItem
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                    }}
                >
                    <ListItemContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {/*
                        <ListItemDecorator sx={{flex: '0 0 auto'}}>
                            {/*  multiselect checkbox here *\/}
                        </ListItemDecorator>
                        */}

                        <div style={{flex: '1 1 auto'}}>
                        {listCell(row, {LinkComponent})}
                        </div>
                        <ListItemDecorator sx={{flex: '0 0 auto', gap: 1, '--ListItem-gap': 0}}>
                            {listActions(row, {view, LinkComponent})}
                            <PrimaryListAction row={row} />
                        </ListItemDecorator>
                    </ListItemContent>
                </ListItem>
                <ListDivider />
                </List>
            ))}
        </Box>
    </>);
}
